import { useCallback, useRef } from 'react';

import { fetcher } from '../../http/Fetcher';
import { captureException } from '../../logger/Logger';
import { useOptionalVisitorId } from '../providers/VisitorProvider';
import { getStoredVisitorId } from '../utils/visitorId';
import { collectVisitorMetadata } from '../utils/visitorMetadata';

const upsertVisitor = async (visitorId: string): Promise<void> => {
    try {
        const metadata = await collectVisitorMetadata();
        await fetcher({
            endpoint: `/api/visitors/${encodeURIComponent(visitorId)}`,
            method: 'PUT',
            body: JSON.stringify({ metadata }),
        });
    } catch (error) {
        captureException(error as Error);
    }
};

/**
 * Upserts the current visitor without blocking the caller. Uses the
 * VisitorProvider id when this component is mounted under it, and otherwise
 * an id already in storage. Does not mint an id, so a screen outside the
 * provider does nothing until one exists. Runs at most once per mount.
 */
export const useUpsertVisitor = (): (() => void) => {
    const contextVisitorId = useOptionalVisitorId();
    const hasUpsertedRef = useRef(false);

    return useCallback(() => {
        if (hasUpsertedRef.current) {
            return;
        }
        const visitorId = contextVisitorId ?? getStoredVisitorId();
        if (visitorId == null) {
            return;
        }
        hasUpsertedRef.current = true;
        void upsertVisitor(visitorId);
    }, [contextVisitorId]);
};
